import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, MenuItem, Tooltip, Divider, Chip, LinearProgress, Alert, Tabs, Tab, Grid, useMediaQuery, useTheme, Pagination } from '@mui/material';
import { Add, Delete, Edit, Save, Refresh, Insights, WarningAmber, AccountBalance, Payment, Receipt, Upload, ArrowUpward, ArrowDownward, AddIcCall, Sell } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../services/api';
import type { Customer, CustomerAnalytics, CustomerInactivityAlert, CustomerAccountSummary, CustomerStatement, CustomerOrderBalance, CustomerPaymentDto, RegisterCustomerPayment } from '../types';
import TextField from '../components/common/TextField';
import ScheduleCallDialog from '../components/commercial/ScheduleCallDialog';
import CustomerPricesDialog from '../components/commercial/CustomerPricesDialog';
import { formatMoney } from '../components/commercial/contact';

const REPURCHASE_LABEL: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'info' }> = {
  ACTIVO: { label: '🟢 Activo', color: 'success' },
  PROXIMO: { label: '🟡 Próximo', color: 'warning' },
  CONTACTAR: { label: '🔴 Contactar', color: 'error' },
  NUEVO: { label: 'Nuevo', color: 'info' },
};

const customerTypeOptions = [
  { value: 'DIETETICA', label: 'Dietética' },
  { value: 'FARMACIA', label: 'Farmacia' },
  { value: 'PERFUMERIA', label: 'Perfumería' },
  { value: 'ESTETICA', label: 'Estética' },
  { value: 'DISTRIBUIDORA', label: 'Distribuidora' },
  { value: 'OTRO', label: 'Otro' },
  { value: 'B2B_Estrategico', label: 'B2B - Estratégico' },
  { value: 'B2B_NoEstrategico', label: 'B2B - No estratégico' },
  { value: 'B2B_Clubs', label: 'B2B - Clubs' },
  { value: 'B2C_ML', label: 'B2C - Mercado Libre' },
  { value: 'B2C_Web', label: 'B2C - Página Web' },
  { value: 'B2B', label: 'B2B (genérico)' },
  { value: 'B2C', label: 'B2C (genérico)' },
];

export const CustomersPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [callCustomer, setCallCustomer] = useState<{ id: number; name: string } | null>(null);
  const [priceLists, setPriceLists] = useState<{ id: number; name: string }[]>([]);
  const [repurchase, setRepurchase] = useState<Record<number, { status: string; daysSinceLastOrder: number; avgDaysBetweenOrders?: number | null }>>({});
  const [pricesCustomer, setPricesCustomer] = useState<{ id: number; name: string } | null>(null);
  useEffect(() => {
    api.getPriceLists().then(l => setPriceLists(l)).catch(() => setPriceLists([]));
    api.getRepurchaseStatus()
      .then(rows => setRepurchase(Object.fromEntries(rows.map(r => [r.customerId, r]))))
      .catch(() => setRepurchase({}));
  }, []);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [items, setItems] = useState<Customer[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const totalPages = Math.ceil(totalCount / pageSize);
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [sellerFilter, setSellerFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [featureFlags, setFeatureFlags] = useState<Set<string>>(new Set());
  const [flagsReady, setFlagsReady] = useState(false);

  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState<Customer | null>(null);
  const [form, setForm] = useState<any>({ legalName: '', cuit: '', contactName: '', phone: '', email: '', customerType: '', status: 'Activo' });
  const [sellers, setSellers] = useState<{ id: number; name: string }[]>([]);

  const [alerts, setAlerts] = useState<CustomerInactivityAlert[]>([]);
  const [alertsDays, setAlertsDays] = useState(30);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsError, setAlertsError] = useState<string | null>(null);
  const [alertsGeneratedAt, setAlertsGeneratedAt] = useState<string | null>(null);

  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState<CustomerAnalytics | null>(null);
  const [analyticsCustomer, setAnalyticsCustomer] = useState<Customer | null>(null);

  // Cuenta corriente state
  const [accountOpen, setAccountOpen] = useState(false);
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountCustomer, setAccountCustomer] = useState<Customer | null>(null);
  const [accountSummary, setAccountSummary] = useState<CustomerAccountSummary | null>(null);
  const [pendingOrders, setPendingOrders] = useState<CustomerOrderBalance[]>([]);
  const [payments, setPayments] = useState<CustomerPaymentDto[]>([]);
  const [accountTab, setAccountTab] = useState(0);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [paymentForm, setPaymentForm] = useState<RegisterCustomerPayment>({ amount: 0, paymentMethod: 'Efectivo' });
  const [paymentAttachments, setPaymentAttachments] = useState<{ fileName: string; contentType: string; base64: string }[]>([]);
  const [paymentSaving, setPaymentSaving] = useState(false);

  const load = async (targetPage = page, targetPageSize = pageSize) => {
    setLoading(true); setError(null);
    try {
      const res = await api.getCustomers({ page: targetPage, pageSize: targetPageSize, search, type, status });
      setItems(res.customers);
      setTotalCount(res.totalCount);
      setPage(res.page || targetPage);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(1); }, []);
  useEffect(() => { load(page, pageSize); }, [page, pageSize]);
  useEffect(() => {
    if (page !== 1) {
      setPage(1);
      return;
    }
    load(1);
  }, [type, status, search]);

  useEffect(() => {
    (async () => {
      try {
        const flags = await api.getFeatureFlags();
        setFeatureFlags(new Set((flags || []).filter(f => f.enabled).map(f => f.key.toLowerCase())));
      } catch {
        setFeatureFlags(new Set());
      }
      setFlagsReady(true);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getUsers({ role: 'Seller' });
        setSellers((res.items || []).map(u => ({ id: u.id, name: u.name })));
      } catch {
        setSellers([]);
      }
    })();
  }, []);

  const hasFlag = (key: string) => featureFlags.has(key.toLowerCase());

  const distinctSellers = useMemo(() => Array.from(new Set(items.map(c => c.assignedSeller).filter(Boolean))).sort() as string[], [items]);

  const sortedItems = useMemo(() => {
    const filtered = items.filter(c => !sellerFilter || c.assignedSeller === sellerFilter);
    return [...filtered].sort((a, b) => {
      const cmp = (a.legalName || '').localeCompare(b.legalName || '');
      return sortOrder === 'asc' ? cmp : -cmp;
    });
  }, [items, sortOrder, sellerFilter]);

  const duplicateCuits = useMemo(() => {
    const counts = new Map<string, number>();
    items.forEach(c => { if (c.cuit) counts.set(c.cuit, (counts.get(c.cuit) || 0) + 1); });
    return new Set(Array.from(counts.entries()).filter(([_, count]) => count > 1).map(([cuit]) => cuit));
  }, [items]);

  const duplicateLegalNames = useMemo(() => {
    const counts = new Map<string, number>();
    items.forEach(c => { if (c.legalName) counts.set(c.legalName.toLowerCase(), (counts.get(c.legalName.toLowerCase()) || 0) + 1); });
    return new Set(Array.from(counts.entries()).filter(([_, count]) => count > 1).map(([name]) => name));
  }, [items]);

  const loadAlerts = async () => {
    if (!hasFlag('customers-inactivity-alerts')) return;
    setAlertsLoading(true); setAlertsError(null);
    try {
      const res = await api.getCustomerInactivityAlerts({ days: alertsDays, top: 100 });
      setAlerts(res.items || []);
      setAlertsGeneratedAt(res.generatedAt || null);
    } catch (e: any) {
      setAlerts([]);
      setAlertsError(e?.response?.data?.message || 'No se pudieron obtener las alertas');
    } finally {
      setAlertsLoading(false);
    }
  };

  useEffect(() => {
    if (!flagsReady) return;
    if (hasFlag('customers-inactivity-alerts')) {
      loadAlerts();
    }
  }, [flagsReady]);

  const openAnalytics = async (customer: Customer) => {
    if (!hasFlag('customers-analytics')) return;
    setAnalyticsCustomer(customer);
    setAnalyticsOpen(true);
    setAnalyticsLoading(true);
    setAnalyticsData(null);
    try {
      const data = await api.getCustomerAnalytics(customer.id);
      setAnalyticsData(data);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo obtener el analytics del cliente');
      setAnalyticsOpen(false);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Link desde la pantalla "Hoy" (Registrar cobro): /customers?account=ID abre la cuenta corriente.
  useEffect(() => {
    const accountId = Number(searchParams.get('account'));
    if (!accountId) return;
    (async () => {
      try {
        const c = await api.getCustomer(accountId);
        if (c) openAccount(c as Customer);
      } catch { /* cliente inexistente: se ignora el link */ }
      searchParams.delete('account');
      setSearchParams(searchParams, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAccount = async (customer: Customer) => {
    setAccountCustomer(customer);
    setAccountOpen(true);
    setAccountLoading(true);
    setAccountError(null);
    setAccountTab(0);
    setPaymentForm({ amount: 0, paymentMethod: 'Efectivo' });
    try {
      const [summary, orders, paymentsRes] = await Promise.all([
        api.getCustomerAccountSummary(customer.id),
        api.getCustomerPendingOrders(customer.id),
        api.getCustomerPayments(customer.id)
      ]);
      setAccountSummary(summary);
      setPendingOrders(orders);
      setPayments(paymentsRes.payments);
    } catch (e: any) {
      setAccountError(e?.response?.data?.message || 'No se pudo cargar la cuenta corriente');
    } finally {
      setAccountLoading(false);
    }
  };

  const refreshAccount = async () => {
    if (!accountCustomer) return;
    setAccountLoading(true);
    setAccountError(null);
    try {
      const [summary, orders, paymentsRes] = await Promise.all([
        api.getCustomerAccountSummary(accountCustomer.id),
        api.getCustomerPendingOrders(accountCustomer.id),
        api.getCustomerPayments(accountCustomer.id)
      ]);
      setAccountSummary(summary);
      setPendingOrders(orders);
      setPayments(paymentsRes.payments);
    } catch (e: any) {
      setAccountError(e?.response?.data?.message || 'Error al actualizar');
    } finally {
      setAccountLoading(false);
    }
  };

  const PAYMENT_ATTACHMENT_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
  const MAX_PAYMENT_ATTACHMENTS = 6;

  const handlePaymentFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    if (files.some(f => !PAYMENT_ATTACHMENT_TYPES.includes(f.type))) {
      setAccountError('Solo se permiten comprobantes en PDF o imagen (PNG, JPG, WEBP)');
      return;
    }
    if (paymentAttachments.length + files.length > MAX_PAYMENT_ATTACHMENTS) {
      setAccountError(`Podés adjuntar hasta ${MAX_PAYMENT_ATTACHMENTS} comprobantes`);
      return;
    }
    try {
      const uploads = await Promise.all(files.map(f => new Promise<{ fileName: string; contentType: string; base64: string }>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve({ fileName: f.name, contentType: f.type, base64: String(reader.result) });
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(f);
      })));
      setPaymentAttachments(prev => [...prev, ...uploads]);
      setAccountError(null);
    } catch {
      setAccountError('No se pudo leer alguno de los archivos');
    }
  };

  const registerPayment = async () => {
    if (!accountCustomer || paymentForm.amount <= 0) return;
    setPaymentSaving(true);
    setAccountError(null);
    try {
      await api.registerCustomerPayment(accountCustomer.id, {
        ...paymentForm,
        paymentDate: new Date().toISOString(),
        attachments: paymentAttachments.length > 0 ? paymentAttachments : undefined,
      });
      setPaymentForm({ amount: 0, paymentMethod: 'Efectivo' });
      setPaymentAttachments([]);
      await refreshAccount();
    } catch (e: any) {
      setAccountError(e?.response?.data?.message || 'Error al registrar pago');
    } finally {
      setPaymentSaving(false);
    }
  };

  const openNew = () => { setEditing(null); setForm({ legalName: '', cuit: '', contactName: '', phone: '', email: '', customerType: '', status: 'Activo' }); setOpenForm(true); };
  const openEdit = (c: Customer) => { setEditing(c); setForm({ ...c, cuit: c.cuit }); setOpenForm(true); };
  const save = async () => {
    try {
      if (!form.legalName?.trim()) { setError('Nombre legal es requerido'); return; }
      if (editing) {
        const upd = await api.updateCustomer(editing.id, {
          legalName: form.legalName,
          cuit: form.cuit || null,
          contactName: form.contactName || null,
          phone: form.phone || null,
          whatsApp: form.whatsApp || null,
          email: form.email || null,
          customerType: form.customerType || null,
          status: form.status || null,
          assignedSeller: form.assignedSeller || null,
          notes: form.notes || null,
          discountPct: Number(form.discountPct || 0),
          paymentTerms: form.paymentTerms || null,
          creditLimit: Number(form.creditLimit || 0),
          creditScore: Number(form.creditScore || 0),
          riskCategory: form.riskCategory || null,
          category: form.category || null,
          route: form.route || null,
          priceListId: form.priceListId || null,
          city: form.city || null,
          deliveryAddress: form.deliveryAddress || null,
          branchCount: Number(form.branchCount || 0),
        } as any);
        setItems(prev => prev.map(x => x.id === editing.id ? upd : x));
      } else {
        const created = await api.createCustomer({
          legalName: form.legalName,
          cuit: form.cuit || null,
          contactName: form.contactName || null,
          phone: form.phone || null,
          whatsApp: form.whatsApp || null,
          email: form.email || null,
          customerType: form.customerType || null,
          status: form.status || null,
          assignedSeller: form.assignedSeller || null,
          notes: form.notes || null,
          discountPct: Number(form.discountPct || 0),
          paymentTerms: form.paymentTerms || null,
          creditLimit: Number(form.creditLimit || 0),
          creditScore: Number(form.creditScore || 0),
          riskCategory: form.riskCategory || null,
          category: form.category || null,
          route: form.route || null,
          priceListId: form.priceListId || null,
          city: form.city || null,
          deliveryAddress: form.deliveryAddress || null,
          branchCount: Number(form.branchCount || 0),
        } as any);
        setItems(prev => [created, ...prev]);
        setTotalCount(t => t + 1);
      }
      setOpenForm(false);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al guardar');
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm('¿Eliminar cliente?')) return;
    try {
      await api.deleteCustomer(id);
      setItems(prev => prev.filter(x => x.id !== id));
      setTotalCount(t => Math.max(0, t - 1));
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al eliminar');
    }
  };

  const formatDate = (value?: string | null) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString();
  };

  const typeLabel = (value?: string | null) => customerTypeOptions.find(o => o.value === value)?.label || value || '—';

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
        <Typography variant="h4" fontWeight={700}>Clientes</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <TextField
            size="small"
            label="Buscar"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (page !== 1) setPage(1);
                else load(1);
              }
            }}
            sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
          />
          <TextField size="small" select label="Tipo" value={type} onChange={e => setType(e.target.value)} sx={{ minWidth: { xs: '100%', sm: 160 } }}>
            <MenuItem value="">Todos</MenuItem>
            {customerTypeOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
          <TextField size="small" select label="Estado" value={status} onChange={e => setStatus(e.target.value)} sx={{ minWidth: { xs: '100%', sm: 120 } }}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="Activo">Activo</MenuItem>
            <MenuItem value="Inactivo">Inactivo</MenuItem>
            <MenuItem value="Potencial">Potencial</MenuItem>
          </TextField>
          <TextField size="small" select label="Vendedor" value={sellerFilter} onChange={e => setSellerFilter(e.target.value)} sx={{ minWidth: { xs: '100%', sm: 160 } }}>
            <MenuItem value="">Todos</MenuItem>
            {distinctSellers.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => load(1)}>Filtrar</Button>
            {hasFlag('sales-monday-columns') && (
              <>
                <Button variant="outlined" startIcon={<Upload />} onClick={() => navigate('/customers/import')}>Importar Clientes</Button>
                <Button variant="outlined" startIcon={<Upload />} onClick={() => navigate('/prospects/import')}>Importar Prospectos</Button>
              </>
            )}
            <Button variant="contained" startIcon={<Add />} onClick={openNew}>Nuevo</Button>
          </Stack>
        </Stack>
      </Stack>

      <Typography variant="h6" sx={{ mb: 1, mt: 2 }}>Listado de Clientes</Typography>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {loading && <LinearProgress sx={{ mb: 1 }} />}
          {error && <Typography color="error" sx={{ mb: 1 }}>{error}</Typography>}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <span>Nombre Legal</span>
                      <IconButton size="small" onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}>
                        {sortOrder === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
                      </IconButton>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>CUIT</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Tipo</TableCell>
                  <TableCell>Recompra</TableCell>
                  <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>Estado</TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Contacto</TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Teléfono</TableCell>
                  <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>Email</TableCell>
                  <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>Vendedor</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedItems.map(c => {
                  const isDuplicate = (c.cuit && duplicateCuits.has(c.cuit)) || (c.legalName && duplicateLegalNames.has(c.legalName.toLowerCase()));
                  return (
                    <TableRow key={c.id} sx={{ bgcolor: isDuplicate ? 'rgba(244,67,54,0.08)' : undefined }}>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography>{c.legalName}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      {c.cuit && duplicateCuits.has(c.cuit) ? (
                        <Tooltip title="CUIT duplicado"><span>{c.cuit}</span></Tooltip>
                      ) : (
                        c.cuit
                      )}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {typeLabel(c.customerType)}{c.category ? ` · ${c.category}` : ''}{c.route ? ` · ${c.route}` : ''}
                    </TableCell>
                    <TableCell>
                      {repurchase[c.id] ? (
                        <Tooltip title={repurchase[c.id].avgDaysBetweenOrders ? `Compra cada ~${repurchase[c.id].avgDaysBetweenOrders} días · hace ${repurchase[c.id].daysSinceLastOrder} días` : `Una sola compra, hace ${repurchase[c.id].daysSinceLastOrder} días`}>
                          <Chip size="small" label={REPURCHASE_LABEL[repurchase[c.id].status]?.label || repurchase[c.id].status}
                            color={REPURCHASE_LABEL[repurchase[c.id].status]?.color || 'default'} />
                        </Tooltip>
                      ) : <Typography variant="caption" color="text.secondary">Sin compras</Typography>}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>{c.status}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{c.contactName}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{c.phone}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>{c.email}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>{c.assignedSeller}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0} justifyContent="flex-end">
                        <Tooltip title="Precios pactados">
                          <span><IconButton size="small" onClick={() => setPricesCustomer({ id: c.id, name: c.legalName })}><Sell /></IconButton></span>
                        </Tooltip>
                        <Tooltip title="Agendar llamado">
                          <span><IconButton size="small" onClick={() => setCallCustomer({ id: c.id, name: c.legalName })} sx={{ color: '#0ea5e9' }}><AddIcCall /></IconButton></span>
                        </Tooltip>
                        <Tooltip title="Cuenta Corriente">
                          <span><IconButton size="small" onClick={() => openAccount(c)} color="primary"><AccountBalance /></IconButton></span>
                        </Tooltip>
                        {hasFlag('customers-analytics') && (
                          <Tooltip title="Ver analytics">
                            <span><IconButton size="small" onClick={() => openAnalytics(c)}><Insights /></IconButton></span>
                          </Tooltip>
                        )}
                        <Tooltip title="Editar"><span><IconButton size="small" onClick={() => openEdit(c)}><Edit /></IconButton></span></Tooltip>
                        <Tooltip title="Eliminar"><span><IconButton size="small" onClick={() => remove(c.id)}><Delete /></IconButton></span></Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  );
                })}
                {sortedItems.length === 0 && (
                  <TableRow><TableCell colSpan={10}><Typography variant="body2" color="text.secondary">Sin clientes</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {totalPages > 0 && (
            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2} sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {totalCount} clientes
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" color="text.secondary">Por página:</Typography>
                <TextField
                  select
                  size="small"
                  value={pageSize}
                  onChange={(e) => {
                    const newSize = Number(e.target.value);
                    setPageSize(newSize);
                    setPage(1);
                  }}
                  sx={{ minWidth: 80 }}
                >
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={200}>200</MenuItem>
                </TextField>
              </Stack>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
              />
            </Stack>
          )}
        </CardContent>
      </Card>

      {hasFlag('customers-inactivity-alerts') && (
        <>
          <Typography variant="h6" sx={{ mb: 1, mt: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <WarningAmber color="warning" />
              <span>Alertas de Inactividad</span>
            </Stack>
          </Typography>
          <Card>
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Clientes que no han sido contactados recientemente
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField size="small" type="number" label="Días sin contacto" value={alertsDays} onChange={e => setAlertsDays(Number(e.target.value || 0))} sx={{ width: 160 }} />
                  <Button variant="outlined" startIcon={<Refresh />} onClick={loadAlerts} disabled={alertsLoading}>Actualizar</Button>
                </Stack>
              </Stack>
              {alertsLoading && <LinearProgress />}
              {alertsError && <Typography color="error" sx={{ mt: 1 }}>{alertsError}</Typography>}
              {!alertsLoading && alerts.length > 0 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Cliente</TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Tipo</TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Último contacto</TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Última venta</TableCell>
                        <TableCell align="right">Días</TableCell>
                        <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Órdenes</TableCell>
                        <TableCell align="right">Ventas</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {alerts.map(a => (
                        <TableRow key={a.customerId} hover>
                          <TableCell>{a.customerName}</TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{typeLabel(a.customerType)}</TableCell>
                          <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{formatDate(a.lastContactAt)}</TableCell>
                          <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{formatDate(a.lastSaleAt)}</TableCell>
                          <TableCell align="right">{a.daysInactive}</TableCell>
                          <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{a.totalOrders}</TableCell>
                          <TableCell align="right">${a.totalSales.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {!alertsLoading && alerts.length === 0 && !alertsError && (
                <Typography variant="body2" color="text.secondary">Sin alertas para el rango seleccionado.</Typography>
              )}
              {alertsGeneratedAt && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Generado: {formatDate(alertsGeneratedAt)}
                </Typography>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Editar cliente' : 'Nuevo cliente'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Nombre legal" value={form.legalName} onChange={e => setForm({ ...form, legalName: e.target.value })} required />
            <TextField label="CUIT" value={form.cuit || ''} onChange={e => setForm({ ...form, cuit: e.target.value })} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Contacto" value={form.contactName || ''} onChange={e => setForm({ ...form, contactName: e.target.value })} fullWidth />
              <TextField label="Email" type="email" value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Teléfono" value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} fullWidth />
              <TextField label="WhatsApp" value={form.whatsApp || ''} onChange={e => setForm({ ...form, whatsApp: e.target.value })} fullWidth />
              <TextField select label="Vendedor asignado" value={form.assignedSeller || ''} onChange={e => setForm({ ...form, assignedSeller: e.target.value })} fullWidth>
                <MenuItem value="">-</MenuItem>
                {sellers.map(s => <MenuItem key={s.id} value={s.name}>{s.name}</MenuItem>)}
              </TextField>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField select label="Categoría" value={form.category || ''} onChange={e => setForm({ ...form, category: e.target.value })} fullWidth>
                <MenuItem value="">-</MenuItem>
                {['A', 'B', 'C'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
              <TextField label="Recorrida / zona" value={form.route || ''} onChange={e => setForm({ ...form, route: e.target.value })} fullWidth />
              <TextField label="Locales (PDV)" type="number" value={form.branchCount ?? ''} onChange={e => setForm({ ...form, branchCount: Number(e.target.value) })} fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Dirección de entrega" value={form.deliveryAddress || ''} onChange={e => setForm({ ...form, deliveryAddress: e.target.value })} fullWidth />
              <TextField label="Localidad" value={form.city || ''} onChange={e => setForm({ ...form, city: e.target.value })} fullWidth />
              <TextField select label="Lista de precios" value={form.priceListId || ''} onChange={e => setForm({ ...form, priceListId: e.target.value ? Number(e.target.value) : null })} fullWidth>
                <MenuItem value="">Por defecto</MenuItem>
                {priceLists.map(l => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
              </TextField>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField select label="Tipo" value={form.customerType || ''} onChange={e => setForm({ ...form, customerType: e.target.value })} fullWidth>
                <MenuItem value="">-</MenuItem>
                {customerTypeOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
              <TextField select label="Estado" value={form.status || ''} onChange={e => setForm({ ...form, status: e.target.value })} fullWidth>
                <MenuItem value="">-</MenuItem>
                <MenuItem value="Activo">Activo</MenuItem>
                <MenuItem value="Inactivo">Inactivo</MenuItem>
                <MenuItem value="Potencial">Potencial</MenuItem>
              </TextField>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Límite de crédito" type="number" value={form.creditLimit || 0} onChange={e => setForm({ ...form, creditLimit: e.target.value })} fullWidth />
              <TextField label="Score crédito" type="number" value={form.creditScore || 0} onChange={e => setForm({ ...form, creditScore: e.target.value })} fullWidth />
            </Stack>
            <TextField label="Notas" multiline minRows={2} value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>Cancelar</Button>
          <Button variant="contained" onClick={save} startIcon={<Save />}>Guardar</Button>
        </DialogActions>
      </Dialog>

      <CustomerPricesDialog customer={pricesCustomer} onClose={() => setPricesCustomer(null)} />
      <ScheduleCallDialog open={!!callCustomer} customer={callCustomer} onClose={() => setCallCustomer(null)} />

      <Dialog open={analyticsOpen} onClose={() => setAnalyticsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <span>{analyticsCustomer ? `Cuánto compró · ${analyticsCustomer.legalName}` : 'Analytics de cliente'}</span>
            {analyticsCustomer && (
              <Button size="small" startIcon={<AddIcCall />} onClick={() => setCallCustomer({ id: analyticsCustomer.id, name: analyticsCustomer.legalName })}>
                Agendar llamado
              </Button>
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {analyticsLoading && <LinearProgress />}
          {!analyticsLoading && analyticsData && (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">Lifetime value</Typography>
                    <Typography variant="h5">${analyticsData.totalLifetimeValue.toLocaleString()}</Typography>
                  </CardContent>
                </Card>
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">Unidades totales</Typography>
                    <Typography variant="h5">{analyticsData.totalUnits.toLocaleString()}</Typography>
                  </CardContent>
                </Card>
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">Última orden</Typography>
                    <Typography variant="h6">{formatDate(analyticsData.lastOrderAt)}</Typography>
                  </CardContent>
                </Card>
                {analyticsCustomer && !analyticsData.orderCount && (
                  <Card sx={{ flex: 1 }}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">Score crediticio</Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="h5">{analyticsCustomer.creditScore ?? 0}</Typography>
                        <Typography variant="caption" color="text.secondary">/100</Typography>
                      </Stack>
                      <LinearProgress variant="determinate" value={Math.min(100, Math.max(0, analyticsCustomer.creditScore || 0))} sx={{ mt: 1 }} />
                    </CardContent>
                  </Card>
                )}
              </Stack>

              {analyticsData.orderCount != null && (
                <Grid container spacing={2}>
                  {[
                    { label: 'Compras', value: String(analyticsData.orderCount) },
                    { label: 'Ticket promedio', value: formatMoney(analyticsData.averageTicket) },
                    { label: 'Este mes', value: formatMoney(analyticsData.totalThisMonth) },
                    { label: 'Este año', value: formatMoney(analyticsData.totalThisYear) },
                    { label: 'Compra cada', value: analyticsData.avgDaysBetweenOrders != null ? `~${analyticsData.avgDaysBetweenOrders} días` : '—' },
                    { label: 'Días sin comprar', value: analyticsData.daysSinceLastOrder != null ? String(analyticsData.daysSinceLastOrder) : '—' },
                    { label: 'Próxima compra esperada', value: formatDate(analyticsData.expectedNextOrderAt || undefined) },
                    { label: 'Saldo a cobrar', value: formatMoney(analyticsData.pendingBalance) },
                    { label: 'Exhibidores en su local', value: String(analyticsData.displaysHeld ?? 0) },
                  ].map(k => (
                    <Grid item xs={6} sm={3} key={k.label}>
                      <Typography variant="caption" color="text.secondary">{k.label}</Typography>
                      <Typography variant="subtitle1" fontWeight={700}>{k.value}</Typography>
                    </Grid>
                  ))}
                </Grid>
              )}

              {!!analyticsData.topProducts?.length && (
                <>
                  <Typography variant="subtitle1">Lo que más compra</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Producto</TableCell>
                        <TableCell align="right">Unidades</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Última vez</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analyticsData.topProducts.map(tp => (
                        <TableRow key={tp.productId}>
                          <TableCell>{tp.productName}</TableCell>
                          <TableCell align="right">{tp.units.toLocaleString('es-AR')}</TableCell>
                          <TableCell align="right">{formatMoney(tp.total)}</TableCell>
                          <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{formatDate(tp.lastPurchasedAt)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}

              <Divider />

              <Typography variant="subtitle1">Compras mensuales</Typography>
              {analyticsData.monthlyPurchases.length > 0 && (
                <Box sx={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analyticsData.monthlyPurchases} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(v) => `$${Math.round(v).toLocaleString()}`} />
                      <RechartsTooltip formatter={(v: any) => `$ ${Math.round(Number(v) || 0).toLocaleString('es-AR')}`} />
                      <Legend />
                      <Bar dataKey="deliveredTotal" name="Pagado" fill="#16a34a" stackId="a" />
                      <Bar dataKey="pendingTotal" name="Pendiente de Pago" fill="#f59e0b" stackId="a" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha de Cierre (Mes)</TableCell>
                      <TableCell>Título</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Pago</TableCell>
                      <TableCell>Estado Delivery</TableCell>
                      <TableCell align="right">Saldo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analyticsData.monthlyPurchases.map(mp => (
                      <TableRow key={mp.month}>
                        <TableCell>{mp.month}</TableCell>
                        <TableCell>{(mp as any).title || '—'}</TableCell>
                        <TableCell align="right">${mp.total.toLocaleString()}</TableCell>
                        <TableCell align="right">${mp.deliveredTotal.toLocaleString()}</TableCell>
                        <TableCell>{(mp as any).deliveryStatus || '—'}</TableCell>
                        <TableCell align="right">${mp.pendingTotal.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                    {analyticsData.monthlyPurchases.length === 0 && (
                      <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">Sin datos</Typography></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

            </Stack>
          )}
          {!analyticsLoading && !analyticsData && (
            <Typography variant="body2" color="text.secondary">Selecciona un cliente para ver analytics.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnalyticsOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Cuenta Corriente Dialog */}
      <Dialog open={accountOpen} onClose={() => setAccountOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Cuenta Corriente - {accountCustomer?.legalName}</Typography>
            <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={refreshAccount} disabled={accountLoading}>
              Actualizar
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {accountLoading && <LinearProgress sx={{ mb: 2 }} />}
          {accountError && <Alert severity="error" sx={{ mb: 2 }}>{accountError}</Alert>}

          {accountSummary && (
            <>
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">Saldo Actual</Typography>
                      <Typography variant="h5" color={accountSummary.currentBalance > 0 ? 'error.main' : 'success.main'}>
                        ${accountSummary.currentBalance.toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">Límite de Crédito</Typography>
                      <Typography variant="h5">${accountSummary.creditLimit.toLocaleString()}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">Crédito Disponible</Typography>
                      <Typography variant="h5" color={accountSummary.availableCredit > 0 ? 'success.main' : 'warning.main'}>
                        ${accountSummary.availableCredit.toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">Vencido</Typography>
                      <Typography variant="h5" color={accountSummary.overdueAmount > 0 ? 'error.main' : 'text.primary'}>
                        ${accountSummary.overdueAmount.toLocaleString()}
                      </Typography>
                      {accountSummary.overdueOrdersCount > 0 && (
                        <Typography variant="caption" color="error">
                          ({accountSummary.overdueOrdersCount} órdenes vencidas)
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Tabs value={accountTab} onChange={(_, v) => setAccountTab(v)} sx={{ mb: 2 }}>
                <Tab label="Órdenes Pendientes" icon={<Receipt />} iconPosition="start" />
                <Tab label="Pagos" icon={<Payment />} iconPosition="start" />
                <Tab label="Registrar Pago" icon={<Add />} iconPosition="start" />
              </Tabs>

              {/* Tab 0: Pending Orders */}
              {accountTab === 0 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Referencia</TableCell>
                        <TableCell>Fecha</TableCell>
                        <TableCell>Vencimiento</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Pagado</TableCell>
                        <TableCell align="right">Pendiente</TableCell>
                        <TableCell>Estado</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingOrders.map(order => (
                        <TableRow key={order.orderId} hover>
                          <TableCell>
                            <Typography
                              variant="body2"
                              component="span"
                              sx={{
                                fontFamily: 'monospace',
                                fontWeight: 600,
                                color: 'primary.main',
                                bgcolor: 'primary.50',
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                fontSize: '0.8rem',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: 200,
                                display: 'inline-block',
                              }}
                              title={order.reference}
                            >
                              {order.reference || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>{formatDate(order.orderDate)}</TableCell>
                          <TableCell>
                            {order.paymentDueDate ? formatDate(order.paymentDueDate) : '—'}
                            {order.daysOverdue && order.daysOverdue > 0 && (
                              <Chip size="small" label={`+${order.daysOverdue}d`} color="error" sx={{ ml: 1 }} />
                            )}
                          </TableCell>
                          <TableCell align="right">${order.total.toLocaleString()}</TableCell>
                          <TableCell align="right">${order.paidAmount.toLocaleString()}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                            ${order.remainingBalance.toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={order.paymentStatus || 'Pendiente'}
                              color={order.paymentStatus === 'Pagado' ? 'success' : order.paymentStatus === 'PagoParcial' ? 'warning' : 'default'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {pendingOrders.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7}>
                            <Typography variant="body2" color="text.secondary">Sin órdenes pendientes</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Tab 1: Payments History */}
              {accountTab === 1 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Fecha</TableCell>
                        <TableCell>Método</TableCell>
                        <TableCell>Referencia</TableCell>
                        <TableCell align="right">Monto</TableCell>
                        <TableCell align="right">Aplicado</TableCell>
                        <TableCell>Estado</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payments.map(payment => (
                        <TableRow key={payment.id} hover sx={{ opacity: payment.isVoided ? 0.5 : 1 }}>
                          <TableCell>{formatDate(payment.paymentDate)}</TableCell>
                          <TableCell>{payment.paymentMethod}</TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              component="span"
                              sx={{
                                fontFamily: 'monospace',
                                fontWeight: 600,
                                color: 'text.primary',
                                bgcolor: 'grey.100',
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                fontSize: '0.8rem',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {payment.reference || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">${payment.amount.toLocaleString()}</TableCell>
                          <TableCell align="right">${payment.allocatedAmount.toLocaleString()}</TableCell>
                          <TableCell>
                            {payment.isVoided ? (
                              <Chip size="small" label="Anulado" color="error" />
                            ) : (
                              <Chip size="small" label="Activo" color="success" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {payments.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <Typography variant="body2" color="text.secondary">Sin pagos registrados</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Tab 2: Register Payment */}
              {accountTab === 2 && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>Registrar nuevo pago</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          label="Monto"
                          type="number"
                          fullWidth
                          value={paymentForm.amount}
                          onChange={e => setPaymentForm({ ...paymentForm, amount: Number(e.target.value) })}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          select
                          label="Método de pago"
                          fullWidth
                          value={paymentForm.paymentMethod}
                          onChange={e => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
                        >
                          <MenuItem value="Efectivo">Efectivo</MenuItem>
                          <MenuItem value="Transferencia">Transferencia</MenuItem>
                          <MenuItem value="Cheque">Cheque</MenuItem>
                          <MenuItem value="Tarjeta">Tarjeta</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          label="Referencia"
                          fullWidth
                          value={paymentForm.reference || ''}
                          onChange={e => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                          placeholder="N° cheque, ID transferencia..."
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          label="Notas"
                          fullWidth
                          value={paymentForm.notes || ''}
                          onChange={e => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Button
                            component="label"
                            variant="outlined"
                            size="small"
                            startIcon={<Upload />}
                            disabled={paymentAttachments.length >= MAX_PAYMENT_ATTACHMENTS}
                          >
                            Adjuntar comprobantes
                            <input
                              type="file"
                              hidden
                              multiple
                              accept="application/pdf,image/*"
                              onChange={handlePaymentFiles}
                            />
                          </Button>
                          <Typography variant="caption" color="text.secondary">
                            PDF o imagen, hasta {MAX_PAYMENT_ATTACHMENTS}
                          </Typography>
                        </Stack>
                        {paymentAttachments.length > 0 && (
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                            {paymentAttachments.map((att, idx) => (
                              <Chip
                                key={`${att.fileName}-${idx}`}
                                label={att.fileName}
                                onDelete={() => setPaymentAttachments(prev => prev.filter((_, i) => i !== idx))}
                                size="small"
                              />
                            ))}
                          </Stack>
                        )}
                      </Grid>
                    </Grid>
                    <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        startIcon={<Payment />}
                        onClick={registerPayment}
                        disabled={paymentSaving || paymentForm.amount <= 0}
                      >
                        {paymentSaving ? 'Guardando...' : 'Registrar Pago'}
                      </Button>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      El pago se aplicará automáticamente a las órdenes más antiguas (FIFO).
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccountOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
